import { useSyncExternalStore } from "react"

/**
 * Last pointer type seen on the page ("mouse" | "touch" | "pen"), so tap
 * behaviour follows the input actually used rather than the layout width
 * (an iPad in landscape has the desktop layout but is still tapped by finger).
 */
let lastPointerType: string | null = null
const listeners = new Set<() => void>()

const coarseQuery: MediaQueryList | null =
  typeof window !== "undefined" ? window.matchMedia("(pointer: coarse)") : null

if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      const changed = isTouchType(e.pointerType) !== isTouchType(lastPointerType)
      lastPointerType = e.pointerType
      if (changed) for (const l of listeners) l()
    },
    { capture: true, passive: true },
  )
}

function isTouchType(type: string | null): boolean {
  return type === "touch" || type === "pen"
}

/** True when the primary pointing device is coarse (finger), e.g. phone or tablet. */
export function isCoarsePrimaryPointer(): boolean {
  return coarseQuery?.matches ?? false
}

/**
 * True when the most recent interaction was touch or pen. Before any pointer
 * event has been seen, falls back to `(pointer: coarse)`. Read at event time.
 */
export function isTouchPointer(): boolean {
  if (lastPointerType === null) return isCoarsePrimaryPointer()
  return isTouchType(lastPointerType)
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** Reactive `isTouchPointer()`; re-renders only when touch/mouse input flips. */
export function useTouchPointer(): boolean {
  return useSyncExternalStore(subscribe, isTouchPointer, () => false)
}
